import React from 'react';
import { intlShape } from '../../util/reactIntl';
import { convertMoneyToNumber } from '../../util/currency';
import { humanizeLineItemCode } from '../../util/data';
import { LINE_ITEMS, propTypes } from '../../util/types';
import { useCurrencyFormatter } from './useCurrencyFormatter';

import css from './OrderBreakdown.module.css';

/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `OrderBreakdown` component.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemUnknownItemsMaybe = props => {
  const { lineItems, isProvider, intl } = props;
  const { formatAmount } = useCurrencyFormatter(isProvider ? 'provider' : 'customer');

  // resolve unknown non-reversal line items
  const allItems = lineItems.filter(item => LINE_ITEMS.indexOf(item.code) === -1 && !item.reversal);

  const items = isProvider
    ? allItems.filter(item => item.includeFor.includes('provider'))
    : allItems.filter(item => item.includeFor.includes('customer'));

  return items.length > 0 ? (
    <React.Fragment>
      {items.map((item, i) => {
        const quantity = item.quantity;

        let labelName = humanizeLineItemCode(item.code);
        const translationKey = `OrderBreakdown.${item.code.replace('line-item/', '')}`;
        const hasTranslation = intl.messages[translationKey];
        if (hasTranslation) {
          labelName = intl.formatMessage({ id: translationKey });
        } else if (labelName === 'Credits') {
          labelName = 'Session price';
        } else if (labelName === 'Total credits') {
          labelName = 'Total';
        }

        const label =
          quantity && quantity > 1
            ? `${labelName} x ${quantity}`
            : labelName;

        const formattedTotal = formatAmount(convertMoneyToNumber(item.lineTotal), item.lineTotal?.currency);
        return (
          <div key={`${i}-item.code`} className={css.lineItem}>
            <span className={css.itemLabel}>{label}</span>
            <span className={css.itemValue}>{formattedTotal}</span>
          </div>
        );
      })}
    </React.Fragment>
  ) : null;
};

export default LineItemUnknownItemsMaybe;
